import { useMemo } from 'react'
import {
  CurrentYearDualCharts,
  type DualChartPoint
} from '../../components/charts/CurrentYearDualCharts'
import type { StockMonthlyBucket, StockQuarterlyBucket } from '../../../models/stock.model'

/**
 * Current-year stock chart pair (monthly + quarterly).
 *
 * Series A: **Closing on-hand** — cumulative net (Σ in − Σ out) at the
 * END of the bucket, carrying every prior-year movement as the
 * starting balance for Jan. Matches real on-hand inventory.
 *
 * Series B: **Out (this period)** — Σ of `direction='out'` movements
 * inside this bucket only (NOT cumulative). Empty months render as 0.
 *
 * Kept this filename to avoid call-site churn — the per-page imports
 * still read `StockInOutCharts` even though the shape changed.
 */
const ONHAND_COLOR = '#4F6F52'
const OUT_COLOR = '#BC5B4A'

const MONTH_LABELS = [
  '', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
] as const

interface Props {
  readonly year: number | null
  readonly monthly: ReadonlyArray<StockMonthlyBucket>
  readonly quarterly: ReadonlyArray<StockQuarterlyBucket>
  readonly isLoading: boolean
  readonly error: string | null
  readonly onRetry: () => void
}

export function StockInOutCharts({ year, monthly, quarterly, isLoading, error, onRetry }: Props): JSX.Element {
  const monthlyData = useMemo<DualChartPoint[]>(
    () => monthly.map((m) => ({
      label: MONTH_LABELS[m.month] ?? String(m.month),
      a: m.closing_units,
      b: m.out_units
    })),
    [monthly]
  )
  const quarterlyData = useMemo<DualChartPoint[]>(
    () => quarterly.map((q) => ({
      label: `Q${q.quarter}`,
      a: q.closing_units,
      b: q.out_units
    })),
    [quarterly]
  )
  return (
    <CurrentYearDualCharts
      year={year}
      monthly={monthlyData}
      quarterly={quarterlyData}
      isLoading={isLoading}
      error={error}
      onRetry={onRetry}
      seriesA={{ name: 'Closing on-hand', color: ONHAND_COLOR }}
      seriesB={{ name: 'Out (this period)', color: OUT_COLOR }}
      aggregateLabel="stock"
      formatValue={(n) => String(Math.round(n))}
      formatAxis={formatCompact}
      emptyNote="No movements in this year yet."
    />
  )
}

function formatCompact(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`
  return String(Math.round(n))
}
